import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, Popover, Typography } from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import CustomInputField from 'ui-components/CustomInputField';

/**
 * What a plan gets for one feature: included, not included, or a short piece of
 * text such as "50 / month". Three named choices rather than a control that
 * cycles, so the value a cell will take is never a guess.
 */
const CellValuePopover = ({ anchorEl, value, onChange, onClose }) => {
    const theme = useTheme();
    const open = Boolean(anchorEl);
    const isText = typeof value === 'string';
    const [draftText, setDraftText] = React.useState(isText ? value : '');

    React.useEffect(() => {
        if (open) setDraftText(typeof value === 'string' ? value : '');
    }, [open, value]);

    const Choice = ({ icon, label, selected, onClick }) => (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                padding: '0.5rem 0.625rem',
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: selected ? theme.palette.customColors.indigo50 : 'transparent',
                '&:hover': { backgroundColor: selected ? theme.palette.customColors.indigo50 : '#f4f4f5' },
            }}
        >
            {icon}
            <Typography sx={{
                fontSize: '0.875rem',
                fontWeight: selected ? 700 : 500,
                color: selected ? theme.palette.customColors.indigo700 : theme.palette.customColors.g300,
            }}>
                {label}
            </Typography>
        </Box>
    );

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            slotProps={{ paper: { sx: { borderRadius: '10px', padding: '0.5rem', minWidth: '15rem' } } }}
        >
            <Choice
                icon={<Check fontSize="small" sx={{ color: theme.palette.customColors.b300 }} />}
                label="Included"
                selected={value === true}
                onClick={() => { onChange(true); onClose(); }}
            />
            <Choice
                icon={<Close fontSize="small" sx={{ color: theme.palette.customColors.g100 }} />}
                label="Not included"
                selected={value === false}
                onClick={() => { onChange(false); onClose(); }}
            />
            <Box sx={{ padding: '0.5rem 0.625rem 0.25rem' }}>
                <CustomInputField
                    label="Or show text"
                    placeholder="e.g. 50 / month"
                    value={draftText}
                    onChange={(event) => setDraftText(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && draftText.trim()) {
                            onChange(draftText.trim());
                            onClose();
                        }
                    }}
                    onBlur={() => { if (draftText.trim()) onChange(draftText.trim()); }}
                    sx={{ marginBottom: 0 }}
                />
                <Typography sx={{ fontSize: '0.75rem', color: theme.palette.customColors.g200, marginTop: '0.375rem' }}>
                    Press Enter to use this text.
                </Typography>
            </Box>
        </Popover>
    );
};

export default CellValuePopover;
